'use strict';
import get from 'lodash/get';
import has from 'lodash/has';
import omit from 'lodash/omit';
import * as prioritiesActions from '../actions/prioritiesListActions';
import {USER_PRIORITY} from '../enums/USER_PRIORITIES';
import {reduxList as autoList} from '../redux/autoList';
import {reduxList as pinnedList} from '../redux/pinnedList';
import {reduxListExports as priorityExports} from '../redux/priorityExports';
import {AUTO_LIST_NAME, PINNED_LIST_NAME, PRIORITY_EXPORTS_NAME} from '../redux/state';

const autoPrioritiesListActions = autoList.actions;
const autoPriorities = autoList.reducers.reducer;
const autoListReducer = autoList.reducers.loadSuccessReducer;
const pinnedPrioritiesListActions = pinnedList.actions;
const pinnedPriorities = pinnedList.reducers.reducer;
const pinnedListReducer = pinnedList.reducers.loadSuccessReducer;
const priorityExportsReducer = priorityExports.reducers.reducer;

export * from './state';

export function prioritiesListReducers(state = {}, action) {
  switch (action.type) {
    // override loadSuccess actions
    case autoPrioritiesListActions.loadSuccess.TYPE:
    case pinnedPrioritiesListActions.loadSuccess.TYPE:
      return combinedListReducer(state, action);

    case prioritiesActions.sweepList.TYPE:
      return {
        ...state,
        [AUTO_LIST_NAME]: sweepListReducer(state[AUTO_LIST_NAME], true),
        [PINNED_LIST_NAME]: sweepListReducer(state[PINNED_LIST_NAME], true),
      };
    case prioritiesActions.sweepListSuccess.TYPE:
      return {
        ...state,
        [AUTO_LIST_NAME]: sweepListSuccessReducer(state[AUTO_LIST_NAME]),
        [PINNED_LIST_NAME]: sweepListSuccessReducer(state[PINNED_LIST_NAME]),
      };
    case prioritiesActions.sweepListError.TYPE:
      // TODO: need to display error somewhere
      return {
        ...state,
        [AUTO_LIST_NAME]: sweepListReducer(state[AUTO_LIST_NAME], false),
        [PINNED_LIST_NAME]: sweepListReducer(state[PINNED_LIST_NAME], false),
      };

    case prioritiesActions.togglePin.TYPE:
      return togglePinReducer(state, action);

    case prioritiesActions.togglePinSuccess.TYPE:
      return togglePinSuccessReducer(state, action);

    case prioritiesActions.togglePinError.TYPE:
      // TODO: need to display error somewhere
      return togglePinReducer(state, action);

    default:
      return {
        ...state,
        [AUTO_LIST_NAME]: autoPriorities(state[AUTO_LIST_NAME], action),
        [PINNED_LIST_NAME]: pinnedPriorities(state[PINNED_LIST_NAME], action),
        [PRIORITY_EXPORTS_NAME]: priorityExportsReducer(state[PRIORITY_EXPORTS_NAME], action),
      };
  }
}

/**
 * Note: This reducer needs to be kept in sync with the behavior of the loadSuccess on the
 * listState (but not listState.list). The relevant sections are flagged with comments.
 *
 * iterate through action.payload.data
 * extract items that do not match the list's priority
 *  - collect the items that match the other list's priority
 *  - move those items to the other list
 *
 * @param {*} state
 * @param {ReduxAction} action
 * @return {*}
 */
function combinedListReducer(state, action) {

  const primary = action.type === autoPrioritiesListActions.loadSuccess.TYPE ? AUTO_LIST_NAME : PINNED_LIST_NAME;
  const primaryPriority = primary === AUTO_LIST_NAME ? USER_PRIORITY.AUTO_PINNED : USER_PRIORITY.PINNED;
  const primaryList = {};
  let primarySize = 0;
  const currentPrimaryList = state[primary] ? state[primary].list : {};
  const primaryActionCreator = primary === AUTO_LIST_NAME
    ? autoPrioritiesListActions.loadSuccess
    : pinnedPrioritiesListActions.loadSuccess;
  const primaryListReducer = primary === AUTO_LIST_NAME
    ? autoListReducer
    : pinnedListReducer;

  const secondary = action.type === autoPrioritiesListActions.loadSuccess.TYPE ? PINNED_LIST_NAME : AUTO_LIST_NAME;
  const secondaryPriority = secondary === AUTO_LIST_NAME ? USER_PRIORITY.AUTO_PINNED : USER_PRIORITY.PINNED;
  const secondaryList = {};
  let secondarySize = 0;
  const currentSecondaryList = state[secondary] ? state[secondary].list : {};
  const secondaryActionCreator = secondary === AUTO_LIST_NAME
    ? autoPrioritiesListActions.loadSuccess
    : pinnedPrioritiesListActions.loadSuccess;
  const secondaryListReducer = secondary === AUTO_LIST_NAME
    ? autoListReducer
    : pinnedListReducer;

  const data = action.payload.list;
  for (const id in data) {
    if (data.hasOwnProperty(id)) {
      const item = data[id];

      if (item === null) {
        primaryList[id] = null;
        primarySize++;

      } else if (item.priority === primaryPriority) {
        primaryList[id] = item;
        primarySize++;

        if (has(currentSecondaryList, id)) {
          secondaryList[id] = null;
          secondarySize++;
        }

      } else if (item.priority === secondaryPriority) {
        secondaryList[id] = item;
        secondarySize++;

        if (has(currentPrimaryList, id)) {
          primaryList[id] = null;
          primarySize++;
        }

      } else {
        // remove from both

        if (has(currentSecondaryList, id)) {
          secondaryList[id] = null;
          secondarySize++;
        }

        if (has(currentPrimaryList, id)) {
          primaryList[id] = null;
          primarySize++;
        }
      }
    }
  }

  // update lists
  let nextPrimaryState;
  if (primarySize > 0) {
    const action = primaryActionCreator({list: primaryList});
    // mimics listState reducer - keep in sync
    nextPrimaryState = primaryListReducer(state[primary], action);
  } else {
    nextPrimaryState = state[primary];
  }

  let nextSecondaryState;
  if (secondarySize > 0) {
    const action = secondaryActionCreator({list: secondaryList});
    // mimics listState reducer - keep in sync
    nextSecondaryState = secondaryListReducer(state[secondary], action);
  } else {
    nextSecondaryState = state[secondary];
  }

  if (nextPrimaryState === state[primary] && nextSecondaryState === state[secondary]) {
    return state;
  } else {
    return {
      ...state,
      [primary]: nextPrimaryState,
      [secondary]: nextSecondaryState,
    };
  }
}

// sweepList

/**
 * @param {*} state
 * @param {Boolean} flagDeleting true: add flag, false: remove flag
 * @return {{}}
 */
export function sweepListReducer(state = {}, flagDeleting) {
  return {
    ...state,
    list: flagPins(state.list, flagDeleting),
  };
}

/**
 * @param {*} state
 * @return {{}}
 */
export function sweepListSuccessReducer(state = {}) {
  return {
    ...state,
    list: removeCompletePins(state.list),
  };
}

/**
 * @param items
 * @param {Boolean} flagDeleting true: add flag, false: remove flag
 * @return {{}}
 */
export function flagPins(items = {}, flagDeleting) {
  const keys = Object.keys(items);

  return keys.length === 0
    ? items
    : keys.reduce(flagDeleting ? flagCompleteReducer(items) : unFlagCompleteReducer(items), {});
}

function flagCompleteReducer(items) {
  return function (acc, key) {
    const item = items[key];
    if (!item) {
      console.log(`missing item for key ${key}`, item);
    }
    const isDeleting = isComplete(item);
    acc[key] = item.isDeleting === isDeleting ? item : Object.assign({}, item, {isDeleting});
    return acc;
  };
}

function unFlagCompleteReducer(items) {
  return function (acc, key) {
    const item = items[key];
    acc[key] = item.isDeleting === true ? omit(item, ['isDeleting']) : item;
    return acc;
  };
}

function isComplete(item) {
  const status = get(item, 'target.status');
  switch (status) {
    case 'approved':
    case 'complete':
      return true;
    default:
      return false;
  }
}

export function removeCompletePins(items = {}) {
  const keys = Object.keys(items);

  return keys.length === 0
    ? items
    : keys.reduce(removeComplete(items), {});
}

function removeComplete(items) {
  return function (acc, key) {
    const item = items[key];
    if (isComplete(item)) {
      return acc;
    } else {
      acc[key] = item;
      return acc;
    }
  };
}

// togglePin

/**
 * Move item to other list and toggle the priority value.
 *
 * @param {*} state
 * @param {ReduxAction} action
 * @return {Object}
 */
export function togglePinReducer(state = {}, action) {
  const id = action.payload.id;
  const priority = action.payload.priority;
  const flag = action.type === prioritiesActions.togglePin.TYPE;

  // get confirm id exists in state
  const autoHasId = has(state, `${AUTO_LIST_NAME}.list.${id}`);
  const pinnedHasId = has(state, `${PINNED_LIST_NAME}.list.${id}`);

  if (autoHasId && priority === USER_PRIORITY.AUTO_PINNED) {
    return state;
  } else if (pinnedHasId && priority === USER_PRIORITY.PINNED) {
    return state;
  } else if (!autoHasId && !pinnedHasId) {
    // no changes
    return state;
  } else if (autoHasId && pinnedHasId) {
    // toggling seems to be in progress, do nothing
    return state;
  }

  let nextAutoState;
  let nextPinnedState;
  if (autoHasId) {
    const result = moveItem(state[AUTO_LIST_NAME], state[PINNED_LIST_NAME], id, priority, flag);
    if (result === null) {
      return state;
    }
    nextAutoState = result.stateFrom;
    nextPinnedState = result.stateTo;
  } else {
    const result = moveItem(state[PINNED_LIST_NAME], state[AUTO_LIST_NAME], id, priority, flag);
    if (result === null) {
      return state;
    }
    nextAutoState = result.stateTo;
    nextPinnedState = result.stateFrom;
  }

  return {
    ...state,
    [AUTO_LIST_NAME]: nextAutoState,
    [PINNED_LIST_NAME]: nextPinnedState,
  };
}

/**
 * @param {*} stateFrom
 * @param {*} stateTo
 * @param {String} id
 * @param {Number} priority
 * @param {Boolean} flag
 * @return {?{stateFrom: {list: *}, stateTo: {list: {}}}}
 */
function moveItem(stateFrom, stateTo, id, priority, flag) {
  const item = stateFrom.list[id];

  // when requesting, do nothing if the item already matches
  if (!flag && item.priority === priority) {
    return null;
  }

  let nextItem = {...item, priority};

  if (flag) {
    nextItem.isToggling = true;
  } else {
    nextItem = omit(stateFrom.list, ['isToggling']);
  }

  return {
    stateFrom: {
      ...stateFrom,
      list: omit(stateFrom.list, [id]),
    },
    stateTo: {
      ...stateTo,
      list: {...stateTo.list, [id]: nextItem},
    },
  };
}

/**
 * @param {*} state
 * @param {ReduxAction} action
 * @return {Object}
 */
export function togglePinSuccessReducer(state = {}, action) {
  const id = action.payload.id;
  const priority = action.payload.priority;

  const autoHasId = has(state, `${AUTO_LIST_NAME}.list.${id}`);
  const pinnedHasId = has(state, `${PINNED_LIST_NAME}.list.${id}`);

  if (autoHasId && priority === USER_PRIORITY.AUTO_PINNED) {
    return untoggleItem(state, id, AUTO_LIST_NAME, PINNED_LIST_NAME);
  } else if (pinnedHasId && priority === USER_PRIORITY.PINNED) {
    return untoggleItem(state, id, PINNED_LIST_NAME, AUTO_LIST_NAME);
  } else {
    return state;
  }
}

/**
 * @param {*} state
 * @param {String} id
 * @param {String} changeList
 * @param {String} leaveList
 * @return {*}
 */
function untoggleItem(state, id, changeList, leaveList) {
  const item = state[changeList].list[id];
  if (has(item, 'isToggling')) {
    const list = state[changeList].list;
    return {
      ...state,
      [leaveList]: state[leaveList],
      [changeList]: {
        ...state[changeList],
        list: {
          ...list,
          [id]: omit(item, ['isToggling']),
        },
      },
    };
  } else {
    return state;
  }
}



// WEBPACK FOOTER //
// ./src/js/priorities/reducers/prioritiesListReducers.js